// packages/web-ui/src/state/image-views.ts
//
// IV3-S1 / IV3-S3 — `image-views` zustand slice. Owns the per-card lifecycle
// of an LLM-raster ("illustrative-image") card, keyed by the STABLE
// `imageViewId` correlation id that ties together the `image:genStarted`
// (skeleton) and `image:genReady` (resolved) SSE events.
//
// WHY A DEDICATED SLICE (the wrong-bubble fix, IV3-S1):
//   `addRenderedViewToLastAssistant` (server) + `locateAttachTarget`
//   (attach-to-chat-slice.ts) both resolve "the latest / streaming bubble".
//   A 30-second image therefore lands on the WRONG turn after `finishStream`
//   or after the user sends more messages. The fix is to resolve the
//   producing message ONCE on `image:genStarted` (from `turnCorrelationId`)
//   and from then on update the card PURELY by `imageViewId` in this slice —
//   never re-resolving an attach target on `image:genReady`. The inline-cards
//   envelope ("illustrative-image") is mounted on the producing message at
//   genStarted; this slice carries the live status the panel renders.
//
// SCOPE BOUNDARY: this slice tracks card-side image lifecycle state only. The
// NL "change the diagram" POST flows through an INJECTED api adapter (default
// delegates to the views image route, tests inject a mock) — the slice itself
// makes no direct `fetch` call. All public actions are synchronous reducers
// except `requestEdit`, which awaits the injected adapter and returns a
// `Result`-shaped object-literal (CLAUDE.md "Coding Standards").

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import {
  apiCall,
  getDefaultApiContext,
  type ApiContext,
  type ApiError,
} from '../api/client.js';

// ─── Image-edit api adapter (injected; default = views image route) ──────────

/**
 * 202-accepted response from `POST /api/v1/views/:viewId/image` (IV2-RASTER).
 * The route is rate-limited + hashed-audit only — it does NOT return the
 * image; the produced raster arrives later via the `image:genReady` SSE event.
 */
export interface ImageEditAccepted {
  accepted: true;
  sceneContentHash?: string;
}

/**
 * Injected api seam for the NL change box. Default delegates to the views
 * image route; tests inject a mock so NO real gpt-image / Azure call is made.
 */
export interface ImageViewsApiAdapter {
  /**
   * POST the user's natural-language instruction to the IV2 raster route.
   * Returns `Result<ImageEditAccepted, ApiError>` — the produced image lands
   * asynchronously via `image:genReady` (this resolves only the 202 accept).
   */
  requestImageEdit: (
    ctx: ApiContext,
    viewId: string,
    instruction: string,
  ) => Promise<{ ok: true; value: ImageEditAccepted } | { ok: false; error: ApiError }>;
}

export const defaultImageViewsApiAdapter: ImageViewsApiAdapter = {
  requestImageEdit: (ctx, viewId, instruction) =>
    // IV3-CARD / MAJ-12 — the `POST /api/v1/views/:viewId/image` route
    // (now a REAL generation route, Unit A) requires `{ sceneContent }` (the
    // NL instruction the server hashes for audit + feeds the raster engine).
    // The previous `{ instruction }` body failed the route schema.
    apiCall<{ sceneContent: string }, ImageEditAccepted>(
      ctx,
      'POST',
      `/api/v1/views/${encodeURIComponent(viewId)}/image`,
      { sceneContent: instruction },
    ),
};

/**
 * Build the PNG URL for a raster view. The IV2 route family serves the bytes
 * at `GET /api/v1/views/<viewId>/image` (Unit A); the SSE `image:genReady`
 * carries a disk `path` (never bytes), but the SPA always renders through this
 * stable endpoint so reload + cache invalidation work uniformly.
 *
 * CRIT-1 — a `?v=` cache-bust is ALWAYS appended (keyed on the
 * `sceneContentHash` when available, else the monotonic version). Without it
 * the browser serves the prior raster from disk cache after a regenerate
 * rewrites the same `<viewId>/image` bytes in place, so the user never sees
 * the new image. Preference order: explicit cache-key (hash) → version.
 */
export function imageUrlForView(
  viewId: string,
  version?: number,
  cacheKey?: string,
): string {
  const base = `/api/v1/views/${encodeURIComponent(viewId)}/image`;
  // Cache-bust on EVERY render so a regenerate (same route, new bytes) always
  // re-fetches. Prefer the content hash (stable per-bytes) over the version.
  const bust =
    cacheKey !== undefined && cacheKey.length > 0
      ? cacheKey
      : String(version ?? 1);
  return `${base}?v=${encodeURIComponent(bust)}`;
}

// ─── Card status + history ────────────────────────────────────────────────

export type ImageCardStatus = 'generating' | 'ready' | 'error' | 'filtered';

/** One entry in the versioned iterate-history lineage (IV3-S3). */
export interface ImageCardHistoryEntry {
  version: number;
  imageUrl?: string;
  alt?: string;
  createdAtMs: number;
  /** The NL instruction that produced this version (absent for v1). */
  instruction?: string;
}

/** Per-card lifecycle record keyed by `imageViewId`. */
export interface ImageCardState {
  status: ImageCardStatus;
  /** The producing message id, resolved ONCE on genStarted (never re-resolved). */
  messageId: string | null;
  /** Producing session id (for cross-session routing diagnostics). */
  sessionId: string | null;
  /** Producing-turn correlation id (durable-record key on the server). */
  turnCorrelationId: string;
  /** The view this card renders (POST target + PNG/SysML source path). */
  viewId: string;
  /** Resolved PNG URL once `status==='ready'`. */
  imageUrl?: string;
  /** Alt text derived from the SceneDescription. */
  alt?: string;
  mimeType?: string;
  startedAtMs: number;
  readyAtMs?: number;
  errorCode?: string;
  /** Monotonic version — bumps on each successful regenerate. */
  version: number;
  /** Stable lineage id grouping every version of one card. */
  lineageId: string;
  /** Versioned iterate history (one lineage). */
  history: ImageCardHistoryEntry[];
  /** Set when an NL edit is in flight (overlay skeleton; prior image retained). */
  pendingEdit?: string;
  /** Set when `markStale` fires (sceneContentHash diverged). */
  stale?: boolean;
}

export interface ImageViewsSliceState {
  imageCardsById: Record<string, ImageCardState>;
}

export const IMAGE_VIEWS_INITIAL_STATE: ImageViewsSliceState = {
  imageCardsById: {},
};

// ─── Action argument shapes ─────────────────────────────────────────────────

export interface BeginGenArgs {
  imageViewId: string;
  turnCorrelationId: string;
  sessionId?: string | null;
  messageId?: string | null;
  viewId?: string;
  /** Injected clock for deterministic tests. */
  nowMs?: number;
}

export interface ResolveReadyArgs {
  imageViewId: string;
  path: string;
  mimeType?: string;
  alt?: string;
  viewId?: string;
  /**
   * Content hash for the produced raster. Used as the `?v=` cache-bust key so
   * a regenerate (same `<viewId>/image` route, new bytes) always re-fetches.
   * Falls back to the version counter when absent.
   */
  sceneContentHash?: string;
  /** Optional explicit image URL override (else derived from viewId+version). */
  imageUrl?: string;
  nowMs?: number;
}

export interface ResolveErrorArgs {
  imageViewId: string;
  errorCode: string;
}

export interface BeginEditArgs {
  imageViewId: string;
  instruction: string;
}

export interface RehydrateArgs {
  imageViewId: string;
  viewId: string;
  messageId: string;
  turnCorrelationId?: string;
  imageUrl?: string;
  alt?: string;
  version?: number;
  /** Content hash for the `?v=` cache-bust (mirrors {@link ResolveReadyArgs}). */
  sceneContentHash?: string;
  stale?: boolean;
  nowMs?: number;
}

export interface ImageViewsSliceActions {
  /**
   * Mount a 'generating' record, or re-enter 'generating' on a regeneration.
   * Keyed by `imageViewId` + `turnCorrelationId`: a second genStarted with the
   * SAME `turnCorrelationId` is an idempotent bus-replay no-op (mirrors
   * inline-cards `attachCard`); a genStarted with a NEW `turnCorrelationId` for
   * an existing card is a regeneration — it flips the card back to 'generating'
   * (re-arming the skeleton overlay + elapsed timer) while retaining the prior
   * image so it stays visible underneath.
   */
  beginGen: (args: BeginGenArgs) => void;
  /**
   * Flip the card to 'ready', set the image URL, push a history entry, bump
   * the version. Clears any `pendingEdit`. No attach-target re-resolution.
   */
  resolveReady: (args: ResolveReadyArgs) => void;
  /**
   * Flip to 'error' (or 'filtered' for IMAGE_CONTENT_FILTERED). RETAINS the
   * prior ready image (IV3-S3 "prior image retained") and clears pendingEdit.
   */
  resolveError: (args: ResolveErrorArgs) => void;
  /**
   * Begin an NL edit: set pendingEdit + status 'generating' WITHOUT clearing
   * the displayed image (skeleton overlays; chat input never blocked).
   */
  beginEdit: (args: BeginEditArgs) => void;
  /** Clear a pending edit (cancel BEFORE egress = no charge, IV3-S3). */
  cancelEdit: (imageViewId: string) => void;
  /** Mark a card stale when `sceneContentHash` diverges. */
  markStale: (imageViewId: string) => void;
  /** Seed a 'ready' record on transcript reload (from the view manifest). */
  rehydrateFromManifest: (args: RehydrateArgs) => void;
  /**
   * Fire the NL change box POST through the injected adapter (202 accept).
   * Sets `pendingEdit` + 'generating' first; on a non-OK accept, flips to
   * 'error' and clears the pending edit. The produced image lands later via
   * `image:genReady` → `resolveReady`.
   */
  requestEdit: (
    imageViewId: string,
    instruction: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Test-only — reset slice state (preserves action refs). */
  _resetImageViews: () => void;
}

export type ImageViewsSlice = ImageViewsSliceState & ImageViewsSliceActions;

// ─── Slice config ────────────────────────────────────────────────────────────

export interface ImageViewsSliceConfig {
  api?: ImageViewsApiAdapter;
  apiContext?: ApiContext;
  now?: () => number;
}

// ─── Slice creator ────────────────────────────────────────────────────────

export const createImageViewsSlice =
  (config: ImageViewsSliceConfig = {}): StateCreator<ImageViewsSlice> =>
  (set, get) => {
    const api = config.api ?? defaultImageViewsApiAdapter;
    const ctx = config.apiContext ?? safeDefaultApiContext();
    const now = config.now ?? ((): number => Date.now());

    return {
      ...IMAGE_VIEWS_INITIAL_STATE,

      beginGen: (args) => {
        const existing = get().imageCardsById[args.imageViewId];
        if (existing !== undefined) {
          // A genStarted for an id we already track is EITHER a bus replay of
          // the SAME turn (idempotent no-op) OR a genuine REGENERATION of the
          // card — the "Generate LLM visualization" button, a layout-wish, an
          // adaptive escalation, or the NL "change the diagram" box. We tell the
          // two apart by `turnCorrelationId` (fresh per POST, stable per replay).
          if (existing.turnCorrelationId === args.turnCorrelationId) {
            // Same turn → true replay → no-op (mirrors inline-cards `attachCard`).
            return;
          }
          // Fresh turn → REGENERATION. Flip the card back to 'generating' so the
          // panel shows its skeleton overlay again; without this the stale image
          // lingers with NO spinner for the tens of seconds until genReady lands,
          // which reads as "nothing happened". RETAIN imageUrl/alt/version/
          // history so the prior render stays visible under the overlay (chat is
          // never blocked) and re-arm the elapsed timer via a fresh startedAtMs.
          // `pendingEdit` is left intact so a concurrent NL-box edit still
          // records its instruction on genReady; identity fields (messageId /
          // sessionId / lineageId) are preserved — the producing message is
          // resolved ONCE at first genStarted and never re-resolved.
          const restartedAtMs = args.nowMs ?? now();
          const regenerating: ImageCardState = {
            ...existing,
            status: 'generating',
            turnCorrelationId: args.turnCorrelationId,
            startedAtMs: restartedAtMs,
            errorCode: undefined,
          };
          set((s) => ({
            ...s,
            imageCardsById: {
              ...s.imageCardsById,
              [args.imageViewId]: regenerating,
            },
          }));
          return;
        }
        const startedAtMs = args.nowMs ?? now();
        const record: ImageCardState = {
          status: 'generating',
          messageId: args.messageId ?? null,
          sessionId: args.sessionId ?? null,
          turnCorrelationId: args.turnCorrelationId,
          viewId: args.viewId ?? args.imageViewId,
          startedAtMs,
          version: 0,
          lineageId: args.imageViewId,
          history: [],
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [args.imageViewId]: record },
        }));
      },

      resolveReady: (args) => {
        const prev = get().imageCardsById[args.imageViewId];
        // AR-6 — strict no-op when no record exists. A cross-session
        // ring-replay of `image:genReady` (the SSE last-event-id replay
        // window during a session switch) must NOT mint an orphan card on
        // the newly-active session. The legitimate seed path is
        // `beginGen` (live turn) or `rehydrateFromManifest` (reload); a bare
        // genReady for an unknown id is dropped.
        if (prev === undefined) return;
        const readyAtMs = args.nowMs ?? now();
        const nextVersion = prev.version + 1;
        const viewId = args.viewId ?? prev.viewId ?? args.imageViewId;
        const imageUrl =
          args.imageUrl ??
          imageUrlForView(viewId, nextVersion, args.sceneContentHash);
        const instruction = prev.pendingEdit;
        const historyEntry: ImageCardHistoryEntry = {
          version: nextVersion,
          imageUrl,
          alt: args.alt,
          createdAtMs: readyAtMs,
          ...(instruction !== undefined ? { instruction } : {}),
        };
        const base: ImageCardState = prev;
        const next: ImageCardState = {
          ...base,
          status: 'ready',
          viewId,
          imageUrl,
          alt: args.alt ?? base.alt,
          mimeType: args.mimeType ?? base.mimeType,
          readyAtMs,
          version: nextVersion,
          history: [...base.history, historyEntry],
          // Clear pending edit + any prior error once a fresh image lands.
          pendingEdit: undefined,
          errorCode: undefined,
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [args.imageViewId]: next },
        }));
      },

      resolveError: (args) => {
        const prev = get().imageCardsById[args.imageViewId];
        if (prev === undefined) return;
        const status: ImageCardStatus =
          args.errorCode === 'IMAGE_CONTENT_FILTERED' ? 'filtered' : 'error';
        const next: ImageCardState = {
          ...prev,
          status,
          errorCode: args.errorCode,
          // Retain the prior ready image (imageUrl/history untouched) so the
          // user keeps the last good render under the error message.
          pendingEdit: undefined,
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [args.imageViewId]: next },
        }));
      },

      beginEdit: (args) => {
        const prev = get().imageCardsById[args.imageViewId];
        if (prev === undefined) return;
        const next: ImageCardState = {
          ...prev,
          status: 'generating',
          pendingEdit: args.instruction,
          // Re-arm the elapsed timer + timeout backstop from the edit start
          // (mirrors the regeneration branch of `beginGen`). Without this, an NL
          // edit on an image older than IMAGE_GEN_TIMEOUT_MS would inherit the
          // original `startedAtMs` and the panel would immediately flash the
          // "taking longer than expected / Retry" state instead of the skeleton.
          startedAtMs: now(),
          // Keep imageUrl so the displayed image stays visible under the overlay.
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [args.imageViewId]: next },
        }));
      },

      cancelEdit: (imageViewId) => {
        const prev = get().imageCardsById[imageViewId];
        if (prev === undefined) return;
        // Cancel BEFORE egress releases the reservation (no charge). Revert to
        // the prior terminal status: 'ready' when a prior image exists, else
        // back to the initial 'generating' wait is meaningless, so fall to
        // 'ready' only when an image is present; otherwise leave as 'error'.
        const next: ImageCardState = {
          ...prev,
          status: prev.imageUrl !== undefined ? 'ready' : prev.status,
          pendingEdit: undefined,
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [imageViewId]: next },
        }));
      },

      markStale: (imageViewId) => {
        const prev = get().imageCardsById[imageViewId];
        if (prev === undefined) return;
        set((s) => ({
          ...s,
          imageCardsById: {
            ...s.imageCardsById,
            [imageViewId]: { ...prev, stale: true },
          },
        }));
      },

      rehydrateFromManifest: (args) => {
        const readyAtMs = args.nowMs ?? now();
        const version = args.version ?? 1;
        const imageUrl =
          args.imageUrl ??
          imageUrlForView(args.viewId, version, args.sceneContentHash);
        const record: ImageCardState = {
          status: 'ready',
          messageId: args.messageId,
          sessionId: null,
          turnCorrelationId: args.turnCorrelationId ?? '',
          viewId: args.viewId,
          imageUrl,
          alt: args.alt,
          startedAtMs: readyAtMs,
          readyAtMs,
          version,
          lineageId: args.imageViewId,
          history: [
            {
              version,
              imageUrl,
              alt: args.alt,
              createdAtMs: readyAtMs,
            },
          ],
          ...(args.stale === true ? { stale: true } : {}),
        };
        set((s) => ({
          ...s,
          imageCardsById: { ...s.imageCardsById, [args.imageViewId]: record },
        }));
      },

      requestEdit: async (imageViewId, instruction) => {
        const card = get().imageCardsById[imageViewId];
        if (card === undefined) {
          return { ok: false, message: 'image card not found' };
        }
        // Optimistically mark the edit pending (skeleton overlays the image).
        get().beginEdit({ imageViewId, instruction });
        const res = await api.requestImageEdit(ctx, card.viewId, instruction);
        if (!res.ok) {
          // POST rejected (rate-limit / provider) — surface as error, retain
          // the prior image, clear the pending edit.
          get().resolveError({ imageViewId, errorCode: res.error.code });
          return { ok: false, message: res.error.message };
        }
        // 202 accepted — the produced image lands via `image:genReady`.
        return { ok: true };
      },

      _resetImageViews: () => {
        set((s) => ({ ...s, ...IMAGE_VIEWS_INITIAL_STATE }));
      },
    };
  };

/** Vanilla-store factory for tests / per-mount isolation. */
export function createImageViewsStore(config: ImageViewsSliceConfig = {}) {
  return createStore<ImageViewsSlice>()(createImageViewsSlice(config));
}

// ─── Selectors ────────────────────────────────────────────────────────────

export function selectImageCard(imageViewId: string) {
  return (s: ImageViewsSliceState): ImageCardState | undefined =>
    s.imageCardsById[imageViewId];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function safeDefaultApiContext(): ApiContext {
  try {
    return getDefaultApiContext();
  } catch {
    return { baseUrl: '', token: null };
  }
}
